import {
    Activity, Award, Bot, Briefcase, ChevronRight,
    Clock, DollarSign, Edit3, MessageSquare, Star,
    TrendingUp, User, Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import { API_URL } from '../config/api'
import './AgentDetail.css'

interface AgentProfile {
  id: number
  // API field names
  agent_name?: string
  name?: string
  personality?: string
  persona?: string
  domain?: string
  speaking_style?: string
  bio?: string
  dream?: string
  status: string
  level: number
  total_xp: number
  eq_score?: number
  tasks_completed?: number
  success_rate?: number
  avg_quality?: number
  earnings?: number
  hourly_rate?: number
  is_for_hire?: boolean
  is_rentable?: number
  cloud_api_provider?: string
  provider?: string
  llm_type?: string
  tags?: string[]
  interests?: string[]
  created_at: string
  avatar?: string
  avatar_url?: string
  cover_color?: string
}

interface AgentOwner {
  user_id?: number
  id?: number
  username: string
  display_name: string
  avatar?: string
  account_type?: string
  type?: string
}

interface AgentPerformance {
  tasks_completed: number
  success_rate: number
  avg_quality: number
  response_time: number
  total_earnings: number
  this_month_earnings: number
}

interface AgentProject {
  id: number
  title: string
  status: string
  project_type: string
  quality_score: number
  created_at: string
}

interface AgentBadge {
  id: string
  name: string
  icon: string
  description: string
  earned_at: string
}

interface AgentActivity {
  id: string
  event_type: string
  title: string
  timestamp: string
}

interface AgentProfileResponse {
  agent: AgentProfile
  owner: AgentOwner
  performance: AgentPerformance
  projects: AgentProject[]
  badges: AgentBadge[]
  activity: AgentActivity[]
}

function timeAgo(ts: string) {
  if (!ts) {return ""}
  const diff = Date.now() - new Date(ts).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) {return "just now"}
  if (m < 60) {return `${m}m ago`}
  const h = Math.floor(m / 60)
  if (h < 24) {return `${h}h ago`}
  return `${Math.floor(h / 24)}d ago`
}

function statusColor(s: string) {
  const map: Record<string, string> = {
    active: "#10b981", idle: "#f59e0b", busy: "#3b82f6", offline: "#94a3b8"
  }
  return map[s] ?? "#94a3b8"
}

// ─── Memory Tab ──────────────────────────────────────────────────────────────
function MemoryTab({ agentId }: { agentId: number }) {
  const [memories, setMemories] = useState<Array<{ memory_type: string; content: string; access_count: number; accessed_at: string }>>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('token')
    fetch(`${API_URL}/api/agents/${agentId}/memory`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => r.ok ? r.json() : { memories: [] })
      .then(d => { setMemories((d as { memories?: typeof memories }).memories ?? []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [agentId])

  const TYPE_COLORS: Record<string, string> = {
    social: '#3b82f6', flzhub: '#8b5cf6', research: '#10b981',
    work: '#f59e0b', chat: '#ec4899'
  }

  if (loading) {return <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>}
  if (!memories.length) {return (
    <div className="ad-empty"><Zap size={32} /><p>No memory yet — agent will learn as it works</p></div>
  )}

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {memories.map((m, i) => (
        <div key={i} style={{ padding: '12px 14px', background: 'var(--body-bg)', borderRadius: 8, borderLeft: `3px solid ${TYPE_COLORS[m.memory_type] ?? '#6b7280'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: (TYPE_COLORS[m.memory_type] ?? '#6b7280') + '20', color: TYPE_COLORS[m.memory_type] ?? '#6b7280', fontWeight: 600, textTransform: 'capitalize' }}>
              {m.memory_type}
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>
              {m.access_count} events · {m.accessed_at ? new Date(m.accessed_at).toLocaleDateString() : ''}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {m.content?.slice(-400) ?? ''}
          </div>
        </div>
      ))}
    </div>
  )
}

type ActiveTab = "projects" | "activity" | "memory"

export default function AgentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [data, setData] = useState<AgentProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ActiveTab>("projects")
  const [bondData, setBondData] = useState<{ bond_level: number; bond_xp: number; next_level_xp: number } | null>(null)

  useEffect(() => {
    if (!id) {return}
    const token = localStorage.getItem("token")
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

     
    setLoading(true)
    fetch(`${API_URL}/api/agents/${id}/profile`, { headers })
      .then(r => {
        if (!r.ok) {throw new Error(r.status === 404 ? "404" : "error")}
        return r.json() as Promise<AgentProfileResponse>
      })
      .then(d => {
        setData(d)
        setLoading(false)
        // Bond level fetch (optional, don't block main load)
        if (token) {
          fetch(`${API_URL}/api/chat/bond/${id}`, { headers })
            .then(r => r.ok ? r.json() : null)
            .then(b => { if (b) { setBondData(b as { bond_level: number; bond_xp: number; next_level_xp: number }) } })
            .catch(() => undefined)
        }
      })
      .catch((e: Error) => { setError(e.message); setLoading(false) })
  }, [id])

  if (loading) {return (
    <MainLayout>
      <div className="ad-page">
        <div className="ad-container">
          <div className="ad-skeleton" style={{ height: 200, borderRadius: 20 }} />
          <div className="ad-skeleton-grid">
            <div className="ad-skeleton" style={{ height: 400, borderRadius: 14 }} />
            <div className="ad-skeleton" style={{ height: 400, borderRadius: 14 }} />
          </div>
        </div>
      </div>
    </MainLayout>
  )}

  if (error || !data) {return (
    <MainLayout>
      <div className="ad-page">
        <div className="ad-container">
          <div className="ad-error-state">
            <div className="ad-error-icon"><Bot size={36} /></div>
            <h2>{error === "404" ? "Agent not found" : "Failed to load agent"}</h2>
            <p>{error === "404" ? "This agent does not exist or has been removed." : "Something went wrong. Please try again."}</p>
            <button className="ad-btn-primary" onClick={() => navigate(-1)}>
              <ChevronRight size={15} style={{ transform: "rotate(180deg)" }} /> Go Back
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  )}

  const { agent, owner, performance, projects, badges, activity } = data

  // API field name normalization
  const agentName = agent.name ?? agent.agent_name ?? "Agent"
  const agentPersona = agent.persona ?? agent.personality ?? agent.domain ?? "AI Agent"
  const agentProvider = agent.provider ?? agent.cloud_api_provider ?? agent.llm_type ?? ""
  const agentTags = agent.tags ?? agent.interests ?? []
  const agentIsForHire = agent.is_for_hire ?? (agent.is_rentable === 1)
  const ownerId = owner.id ?? owner.user_id
  const ownerType = owner.type ?? owner.account_type ?? "user"

  const coverGradient = agent.cover_color
    ? `linear-gradient(135deg, ${agent.cover_color}33, ${agent.cover_color}66)`
    : "linear-gradient(135deg, #667eea33, #764ba266)"

  const avatarInitial = agentName.substring(0, 1).toUpperCase()
  const agentAvatar = agent.avatar ?? agent.avatar_url

  const perfItems = [
    { label: "Success Rate", pct: (performance.success_rate ?? 0) * 100, color: "#10b981" },
    { label: "Avg Quality", pct: ((performance.avg_quality ?? 0) / 10) * 100, color: "#667eea" },
  ]

  return (
    <MainLayout>
      <div className="ad-page">
        <div className="ad-container">

          {/* HERO */}
          <div className="ad-hero">
            <div className="ad-hero-bg" style={{ background: coverGradient }} />
            <div className="ad-hero-content">
              <div className="ad-hero-left">
                <div className="ad-avatar-wrap">
                  <div className="ad-avatar" style={{ background: agent.cover_color ? `linear-gradient(135deg, ${agent.cover_color}, #764ba2)` : "linear-gradient(135deg, #667eea, #764ba2)" }}>
                    {agentAvatar
                      ? <img src={agentAvatar} alt={agentName} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 20 }} />
                      : <span style={{ color: "#fff", fontSize: 36, fontWeight: 800 }}>{avatarInitial}</span>
                    }
                  </div>
                  <div className={`ad-online-dot ${agent.status === "active" ? "online" : ""}`} />
                </div>

                <div className="ad-hero-info">
                  <div className="ad-hero-name-row">
                    <h1 className="ad-name">{agentName}</h1>
                    <span className="ad-status-pill" style={{ background: `${statusColor(agent.status)}18`, color: statusColor(agent.status) }}>
                      {agent.status ?? "active"}
                    </span>
                    {agentIsForHire && (
                      <span className="ad-hire-pill">
                        <DollarSign size={10} /> For Hire
                      </span>
                    )}
                  </div>

                  <p className="ad-personality">{agentPersona}</p>
                  {agent.speaking_style && <p className="ad-speaking-style">&quot;{agent.speaking_style}&quot;</p>}

                  <button className="ad-owner-chip" onClick={() => navigate(`/user/${ownerId ?? owner.username}`)}>
                    <div className="ad-owner-chip-avatar">
                      {owner.avatar
                        ? <img src={owner.avatar} alt={owner.display_name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }} />
                        : <span style={{ fontSize: 10, fontWeight: 700 }}>{(owner.display_name ?? "U").substring(0, 1).toUpperCase()}</span>
                      }
                    </div>
                    <span className="ad-owner-chip-name">{owner.display_name}</span>
                    <span className="ad-owner-chip-user">@{owner.username}</span>
                    <ChevronRight size={13} style={{ color: "var(--text-muted)", marginLeft: "auto" }} />
                  </button>

                  <div className="ad-hero-badges">
                    <span className="ad-badge level"><TrendingUp size={10} /> Level {agent.level ?? 1}</span>
                    <span className="ad-badge xp"><Star size={10} /> {agent.total_xp ?? 0} XP</span>
                    {agent.eq_score !== null && agent.eq_score !== undefined && (
                      <span className="ad-badge eq"><Zap size={10} /> EQ {agent.eq_score.toFixed(1)}</span>
                    )}
                    {agentProvider && (
                      <span className="ad-badge provider">{agentProvider}</span>
                    )}
                  </div>

                  {/* Bond Level Progress Bar */}
                  {bondData && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>
                          Bond Lv.{bondData.bond_level}
                        </span>
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          {bondData.bond_xp} / {bondData.next_level_xp} XP
                        </span>
                      </div>
                      <div style={{ height: '4px', background: 'var(--border-color)', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(100, (bondData.bond_xp / Math.max(bondData.next_level_xp, 1)) * 100)}%`,
                          background: 'linear-gradient(90deg, #6366f1, #ec4899)',
                          borderRadius: '2px',
                          transition: 'width 0.5s ease',
                        }} />
                      </div>
                    </div>
                  )}

                  <div className="ad-hero-actions">
                    <button className="ad-btn-primary" onClick={() => navigate(`/chat?agent=${agent.id}`)}>
                      <MessageSquare size={14} /> Chat with Agent
                    </button>
                    {agentIsForHire && (
                      <button className="ad-btn-hire">
                        <Briefcase size={14} /> Hire Agent
                        {agent.hourly_rate !== null && agent.hourly_rate !== undefined && (
                          <span className="ad-rate-tag">{agent.hourly_rate} FLZ/hr</span>
                        )}
                      </button>
                    )}
                    <button className="ad-btn-primary" style={{ background: "var(--hover-bg)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}
                      onClick={() => navigate(`/agents/${agent.id}/edit`)}>
                      <Edit3 size={14} /> Edit
                    </button>
                  </div>
                </div>
              </div>

              {/* HERO STATS */}
              <div className="ad-hero-stats">
                {[
                  { val: performance.tasks_completed ?? 0, lbl: "Tasks" },
                  { val: `${performance.success_rate ?? 0}%`, lbl: "Success" },
                  { val: (performance.avg_quality ?? 0).toFixed(1), lbl: "Quality" },
                  { val: `${(performance.total_earnings ?? 0).toFixed(0)} FLZ`, lbl: "Earned" },
                ].map(s => (
                  <div key={s.lbl} className="ad-hero-stat">
                    <div className="ad-hero-stat-val">{s.val}</div>
                    <div className="ad-hero-stat-lbl">{s.lbl}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* STAT CARDS */}
          <div className="ad-stats-row">
            {[
              { icon: <Activity size={18} />, val: performance.tasks_completed ?? 0, lbl: "Tasks Completed", color: "#667eea" },
              { icon: <Star size={18} />,     val: `${(performance.avg_quality ?? 0).toFixed(1)}/10`, lbl: "Avg Quality", color: "#f59e0b" },
              { icon: <Clock size={18} />,    val: `${performance.response_time ?? 0}s`, lbl: "Avg Response", color: "#3b82f6" },
              { icon: <DollarSign size={18} />, val: `${(performance.total_earnings ?? 0).toFixed(0)} FLZ`, lbl: "Total Earned", color: "#10b981" },
            ].map(s => (
              <div key={s.lbl} className="ad-stat-card">
                <div className="ad-stat-card-icon" style={{ background: `${s.color}18`, color: s.color }}>{s.icon}</div>
                <div className="ad-stat-card-body">
                  <div className="ad-stat-card-val">{s.val}</div>
                  <div className="ad-stat-card-lbl">{s.lbl}</div>
                </div>
              </div>
            ))}
          </div>

          {/* MAIN GRID */}
          <div className="ad-grid">
            {/* LEFT */}
            <div className="ad-col-left">

              {/* BIO */}
              {(agent.bio || agent.dream) && (
                <div className="ad-card">
                  <div className="ad-card-header">
                    <User size={15} className="ad-card-icon" />
                    <h3 className="ad-card-title">About</h3>
                  </div>
                  {agent.bio && <p className="ad-bio">{agent.bio}</p>}
                  {agent.dream && (
                    <div className="ad-dream-box">
                      <span className="ad-dream-label">Dream:</span>
                      <span>{agent.dream}</span>
                    </div>
                  )}
                </div>
              )}

              {/* TAGS */}
              {agentTags.length > 0 && (
                <div className="ad-card">
                  <div className="ad-card-header">
                    <Award size={15} className="ad-card-icon" />
                    <h3 className="ad-card-title">Skills & Tags</h3>
                  </div>
                  <div className="ad-tags">
                    {agentTags.map((tag, i) => (
                      <span key={i} className="ad-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* PROJECTS + ACTIVITY + MEMORY TABS */}
              <div className="ad-card ad-card-tabs">
                <div className="ad-tabs">
                  <button className={`ad-tab ${activeTab === "projects" ? "active" : ""}`} onClick={() => setActiveTab("projects")}>
                    <Briefcase size={13} /> Projects
                    <span className="ad-tab-count">{projects.length}</span>
                  </button>
                  <button className={`ad-tab ${activeTab === "activity" ? "active" : ""}`} onClick={() => setActiveTab("activity")}>
                    <Activity size={13} /> Activity
                    <span className="ad-tab-count">{activity.length}</span>
                  </button>
                  <button className={`ad-tab ${activeTab === "memory" ? "active" : ""}`} onClick={() => setActiveTab("memory")}>
                    <Zap size={13} /> Memory
                  </button>
                </div>

                <div className="ad-tab-body">
                  {activeTab === "projects" && (
                    projects.length === 0
                      ? <div className="ad-empty"><Bot size={32} /><p>No projects yet</p></div>
                      : projects.map(p => (
                        <div key={p.id} className="ad-project-row" onClick={() => navigate(`/project/${p.id}`)}>
                          <div className="ad-project-dot" style={{ background: p.status === "active" ? "#10b981" : p.status === "completed" ? "#667eea" : "#94a3b8" }} />
                          <div className="ad-project-info">
                            <div className="ad-project-title">{p.title}</div>
                            <div className="ad-project-meta">{p.project_type} · {timeAgo(p.created_at)}</div>
                          </div>
                          <div className="ad-project-right">
                            <div className="ad-project-quality">{(p.quality_score ?? 0).toFixed(1)}</div>
                            <div className="ad-project-status" style={{ color: p.status === "active" ? "#10b981" : "#94a3b8" }}>{p.status}</div>
                          </div>
                          <ChevronRight size={14} className="ad-project-arrow" />
                        </div>
                      ))
                  )}

                  {activeTab === "activity" && (
                    activity.length === 0
                      ? <div className="ad-empty"><Activity size={32} /><p>No activity yet</p></div>
                      : activity.map((item, idx) => (
                        <div key={item.id} className="ad-activity-row">
                          <div className="ad-activity-dot" />
                          {idx < activity.length - 1 && <div className="ad-activity-line" />}
                          <div className="ad-activity-body">
                            <div className="ad-activity-title">{item.title ?? item.event_type}</div>
                            <div className="ad-activity-meta">{timeAgo(item.timestamp)}</div>
                          </div>
                        </div>
                      ))
                  )}

                  {activeTab === "memory" && (
                    <MemoryTab agentId={agent.id} />
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="ad-col-right">

              {/* OWNER */}
              <div className="ad-card ad-owner-card" onClick={() => navigate(`/user/${ownerId ?? owner.username}`)}>
                <div className="ad-card-header">
                  <User size={15} className="ad-card-icon" />
                  <h3 className="ad-card-title">Owner</h3>
                  <ChevronRight size={14} className="ad-card-arrow" />
                </div>
                <div className="ad-owner-row">
                  <div className="ad-owner-avatar">
                    {owner.avatar
                      ? <img src={owner.avatar} alt={owner.display_name} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 12 }} />
                      : <span style={{ fontSize: 18, fontWeight: 700 }}>{(owner.display_name ?? "U").substring(0, 1).toUpperCase()}</span>
                    }
                  </div>
                  <div className="ad-owner-info">
                    <div className="ad-owner-display">{owner.display_name}</div>
                    <div className="ad-owner-username">@{owner.username}</div>
                  </div>
                  <span className="ad-owner-type-badge">{ownerType}</span>
                </div>
              </div>

              {/* PERFORMANCE */}
              <div className="ad-card">
                <div className="ad-card-header">
                  <TrendingUp size={15} className="ad-card-icon" />
                  <h3 className="ad-card-title">Performance</h3>
                </div>
                <div className="ad-perf-list">
                  {perfItems.map(p => (
                    <div key={p.label} className="ad-perf-item">
                      <div className="ad-perf-label-row">
                        <span>{p.label}</span>
                        <span className="ad-perf-pct">{p.pct.toFixed(0)}%</span>
                      </div>
                      <div className="ad-perf-track">
                        <div className="ad-perf-fill" style={{ width: `${Math.min(100, p.pct)}%`, background: p.color }} />
                      </div>
                    </div>
                  ))}
                  <div className="ad-perf-kv">
                    <span>This Month</span>
                    <span className="ad-perf-val">{(performance.this_month_earnings ?? 0).toFixed(1)} FLZ</span>
                  </div>
                </div>
              </div>

              {/* INFO */}
              <div className="ad-card">
                <div className="ad-card-header">
                  <Bot size={15} className="ad-card-icon" />
                  <h3 className="ad-card-title">Details</h3>
                </div>
                <div className="ad-info-list">
                  {[
                    { label: "Domain",   value: agent.domain ?? "—" },
                    { label: "Provider", value: agentProvider || "—" },
                    { label: "Status",   value: agent.status ?? "active" },
                    { label: "Level",    value: String(agent.level ?? 1) },
                    { label: "Created",  value: agent.created_at ? new Date(agent.created_at).toLocaleDateString() : "—" },
                  ].map(r => (
                    <div key={r.label} className="ad-info-row">
                      <span className="ad-info-label">{r.label}</span>
                      <span className="ad-info-value">{r.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* BADGES */}
              {badges && badges.length > 0 && (
                <div className="ad-card">
                  <div className="ad-card-header">
                    <Award size={15} className="ad-card-icon" />
                    <h3 className="ad-card-title">Badges</h3>
                  </div>
                  <div className="ad-qs-grid">
                    {badges.map(b => (
                      <div key={b.id} className="ad-qs-item" title={b.description}>
                        <div className="ad-qs-val">{b.icon ?? "🏆"}</div>
                        <div className="ad-qs-lbl">{b.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>

        </div>
      </div>
    </MainLayout>
  )
}
